import { DateTime } from 'luxon';
import { useEffect, useState, useRef } from 'react';

import { Rule, RulesMap } from '../../types/Rules';
import { useVoucher } from '../../context/VoucherProdiver';
import { getLineById } from '../../apis';
import { Line } from '../../types/Line';

import InfoDisplay from './InfoDisplay';
import { Currency } from '@flixbus/honeycomb-react';

// Helper function to check if a string is a UUID
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export interface RuleDisplayProps {
  title: string;
  fields: Rule['type'][];
}

const RuleDisplay = ({ title, fields }: RuleDisplayProps) => {
  const {
    data: { rules },
  } = useVoucher();

  const [linesMap, setLinesMap] = useState<Record<string, Line>>({});
  const [loadingLines, setLoadingLines] = useState(true);
  const previousLineIdsRef = useRef<string>('');

  useEffect(() => {
    const loadLines = async () => {
      // Only fetch lines if this component needs to display line rules
      const needsLineData = fields.some(f => f === 'line_blacklist' || f === 'line_whitelist');

      if (!needsLineData) {
        setLoadingLines(false);
        return;
      }

      if (!rules) {
        setLoadingLines(false);
        return;
      }

      const lineRules = rules.filter(
        (r): r is Rule & { lines: string[] } =>
          r.type === 'line_blacklist' || r.type === 'line_whitelist',
      );
      const allLineValues = lineRules.flatMap(rule => rule.lines);

      // Filter only UUID values for API calls
      const uuidLineIds = allLineValues.filter(isUUID);

      // Compare stringified IDs to check if they actually changed
      const currentLineIdsString = JSON.stringify(uuidLineIds.sort());

      if (currentLineIdsString === previousLineIdsRef.current) {
        // Line IDs haven't changed, skip fetching
        return;
      }

      if (uuidLineIds.length === 0) {
        setLoadingLines(false);
        previousLineIdsRef.current = currentLineIdsString;
        return;
      }

      previousLineIdsRef.current = currentLineIdsString;

      try {
        const lineDetails = await Promise.all(uuidLineIds.map(lineId => getLineById(lineId)));
        const map: Record<string, Line> = {};
        lineDetails.forEach(line => {
          map[line.lineId] = line;
        });
        setLinesMap(map);
      } catch {
        // If loading fails, linesMap remains empty and UUIDs will be displayed as fallback
        setLinesMap({});
      } finally {
        setLoadingLines(false);
      }
    };

    loadLines();
  }, [rules, fields]);

  if (!rules || rules.length === 0) return null;

  const renderDate = (str: string) => {
    return DateTime.fromISO(str).setLocale('de').toLocaleString();
  };

  const renderRuleValue = (ruleType: Rule['type'], fieldKey?: 'start' | 'end') => {
    const rule = rules.filter(r => r.type === ruleType)[0];

    if (!rule) return '-';

    switch (rule.type) {
      case 'allowed_countries':
        return rule.countries.join(', ');
      case 'forbidden_countries':
        return rule.countries.join(', ');
      case 'allowed_platforms':
        return rule.platforms.join(', ');
      case 'travel_period':
      case 'blackout_period':
        if (fieldKey === 'start') {
          return rule.start ? renderDate(rule.start) : 'N/A';
        } else if (fieldKey === 'end') {
          return rule.end ? renderDate(rule.end) : 'N/A';
        }
        return `${rule.start ? renderDate(rule.start) : 'N/A'} to ${rule.end ? renderDate(rule.end) : 'N/A'}`;
      case 'direct_ride':
        return 'Direct Ride Only';
      case 'line_blacklist':
      case 'line_whitelist':
        if (loadingLines) return 'Loading...';
        return rule.lines
          .map(lineValue => {
            // If it's a UUID, look up the line details from the map
            if (isUUID(lineValue)) {
              const line = linesMap[lineValue];
              return line ? `${line.code} - ${line.title}` : lineValue;
            }
            // If it's a code (not a UUID), display it directly
            return lineValue;
          })
          .join(', ');
      case 'minimum_tickets_amount':
        return `${rule.count}`;
      case 'minimum_value':
        return <Currency value={rule.value.amount} code={rule.value.currency} />;
      case 'offline_sales':
        return 'Offline Sales Only';
      case 'weekdays_travel':
        return rule.weekDays.join(', ');
      case 'free_ancillaries':
        return rule.ancillaries.join(', ');
      default:
        return '-';
    }
  };

  const expandedFields = fields.flatMap(f => {
    if (f === 'travel_period') {
      return [
        { label: `${RulesMap[f]} since`, value: renderRuleValue(f, 'start') },
        { label: `${RulesMap[f]} until`, value: renderRuleValue(f, 'end') },
      ];
    }
    if (f === 'blackout_period') {
      return [
        { label: 'From', value: renderRuleValue(f, 'start') },
        { label: 'To', value: renderRuleValue(f, 'end') },
      ];
    }
    return [{ label: RulesMap[f], value: renderRuleValue(f) }];
  });

  return <InfoDisplay title={title} fields={expandedFields} />;
};

export default RuleDisplay;
